import { ExternalLink, ArrowUpRight, TrendingUp, Zap } from 'lucide-react';
import type {
  PluginManifest,
  HealthCheckResult,
  InstalledPluginInfo,
  OverviewWidget,
} from '@/types';
import { HealthBadge } from './HealthBadge';
import { OAuthPropertyPicker } from './OAuthPropertyPicker';
import { GoogleTagConnect } from './GoogleTagConnect';

interface UsageData {
  plan: string;
  period: string;
  current: { conversations: number; aiMessages: number; messages: number };
  limits: { conversations: number; aiMessages: number };
  allowed: boolean;
  upsell: {
    reason: string;
    percentUsed: number;
    message: string;
    severity: 'warning' | 'critical';
  } | null;
}

interface PluginOverviewProps {
  manifest: PluginManifest;
  installed: InstalledPluginInfo | null;
  health?: HealthCheckResult;
  projectId: string;
  pluginId: string;
  /**
   * Absolute origin of the project's live storefront (e.g.
   * "https://www.happylivinghostel.com"). Used by `link-card` widgets to
   * resolve relative hrefs like `/sitemap.xml` against the storefront
   * instead of the admin app's own origin. Optional — when absent,
   * relative hrefs are left as-is.
   */
  storefrontOrigin?: string | null;
  usage?: UsageData | null;
  onNavigateToPlans?: () => void;
}

function interpolateUrl(url: string, projectId: string): string {
  return url.replace(/{projectId}/g, projectId).replace(/{pluginId}/g, '');
}

/**
 * Resolve a link-card href:
 *   1. Replace {storefrontOrigin} placeholder if present.
 *   2. If the href is a root-relative path (e.g. `/sitemap.xml`) AND a
 *      storefrontOrigin is available, prefix it. This is the fix for
 *      "Open Sitemap.xml" landing on admin.tyashin.com/sitemap.xml
 *      instead of the customer's live domain.
 *   3. Absolute URLs (http://, https://) and protocol-relative URLs pass
 *      through untouched.
 *   4. When storefrontOrigin is null/empty and href is relative, leave
 *      the value as-is — the browser will resolve against admin's origin
 *      and the user can still see a URL (better than rendering "#").
 */
function resolveLinkCardHref(href: string, storefrontOrigin?: string | null): string {
  if (!href) return '#';
  let out = href;
  if (storefrontOrigin) {
    out = out.replace(/{storefrontOrigin}/g, storefrontOrigin.replace(/\/+$/, ''));
  }
  // Already absolute → done.
  if (/^https?:\/\//i.test(out) || out.startsWith('//')) return out;
  // Relative path + we have an origin → prefix it.
  if (storefrontOrigin && out.startsWith('/')) {
    return `${storefrontOrigin.replace(/\/+$/, '')}${out}`;
  }
  return out;
}

function StatWidget({ widget }: { widget: OverviewWidget }) {
  return (
    <div className="bg-card rounded-xl border p-5">
      <div className="text-muted-foreground mb-1 text-sm">{widget.title}</div>
      <div className="text-2xl font-bold">&mdash;</div>
      {widget.unit && <div className="text-muted-foreground text-xs">{widget.unit}</div>}
    </div>
  );
}

function LinkCardWidget({
  widget,
  projectId,
  storefrontOrigin,
}: {
  widget: OverviewWidget;
  projectId: string;
  storefrontOrigin?: string | null;
}) {
  const interpolated = widget.href ? interpolateUrl(widget.href, projectId) : '';
  const url = interpolated ? resolveLinkCardHref(interpolated, storefrontOrigin) : '#';

  return (
    <div className="bg-card rounded-xl border p-5">
      <h3 className="mb-1 font-medium">{widget.title}</h3>
      {widget.description && (
        <p className="text-muted-foreground mb-3 text-sm">{widget.description}</p>
      )}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary inline-flex items-center gap-1 text-sm font-medium hover:underline"
      >
        {widget.cta || 'Open'}
        <ArrowUpRight className="h-3.5 w-3.5" />
      </a>
    </div>
  );
}

export function PluginOverview({
  manifest,
  installed,
  health,
  projectId,
  pluginId,
  storefrontOrigin,
  usage,
  onNavigateToPlans,
}: PluginOverviewProps) {
  const widgets = manifest.adminPage?.overviewWidgets || [];

  const isOAuthIntegration = manifest.pluginType === 'oauth-integration' && installed?.isEnabled;
  // Google Tag's optional "Connect with Google" — post-install, never the
  // install flow itself (see lib/integrations/google-tag-connect.ts).
  const isGoogleTag = manifest.id === 'google-tag' && Boolean(installed);

  return (
    <div className="space-y-6">
      {isOAuthIntegration && (
        <OAuthPropertyPicker
          manifest={manifest}
          projectId={projectId}
          pluginId={pluginId}
          currentConfig={installed?.config as Record<string, any> | undefined}
        />
      )}
      {isGoogleTag && (
        <GoogleTagConnect
          projectId={projectId}
          currentConfig={installed?.config as Record<string, any> | undefined}
          storefrontOrigin={storefrontOrigin}
        />
      )}

      {/* Upgrade nudge — shown when usage approaches limits */}
      {usage?.upsell && onNavigateToPlans && (
        <div
          className={`flex items-center gap-4 rounded-xl border p-5 ${
            usage.upsell.severity === 'critical'
              ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/10'
              : 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10'
          }`}
        >
          <TrendingUp
            className={`h-8 w-8 flex-shrink-0 ${
              usage.upsell.severity === 'critical' ? 'text-red-500' : 'text-amber-500'
            }`}
          />
          <div className="flex-1">
            <p className="font-medium">
              {usage.upsell.severity === 'critical'
                ? 'Service limit approaching'
                : 'Your business is growing'}
            </p>
            <p className="text-muted-foreground mt-0.5 text-sm">{usage.upsell.message}</p>
          </div>
          <button
            onClick={onNavigateToPlans}
            className="bg-primary text-primary-foreground hover:bg-primary/90 flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            <Zap className="h-4 w-4" />
            View Plans
          </button>
        </div>
      )}

      {/* Quick usage stats on overview */}
      {usage && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="bg-card rounded-xl border p-5">
            <div className="text-muted-foreground mb-1 text-sm">Conversations</div>
            <div className="text-2xl font-bold">{usage.current.conversations.toLocaleString()}</div>
            <div className="text-muted-foreground text-xs">
              {usage.limits.conversations > 0
                ? `of ${usage.limits.conversations.toLocaleString()} (${usage.plan} plan)`
                : 'unlimited'}
            </div>
          </div>
          <div className="bg-card rounded-xl border p-5">
            <div className="text-muted-foreground mb-1 text-sm">AI Messages</div>
            <div className="text-2xl font-bold">{usage.current.aiMessages.toLocaleString()}</div>
            <div className="text-muted-foreground text-xs">
              {usage.limits.aiMessages > 0
                ? `of ${usage.limits.aiMessages.toLocaleString()} (${usage.plan} plan)`
                : 'unlimited'}
            </div>
          </div>
          <div className="bg-card rounded-xl border p-5">
            <div className="text-muted-foreground mb-1 text-sm">Total Messages</div>
            <div className="text-2xl font-bold">{usage.current.messages.toLocaleString()}</div>
            <div className="text-muted-foreground text-xs">{usage.period}</div>
          </div>
        </div>
      )}

      {/* Health status */}
      {manifest.adminPage?.showHealthStatus && health && (
        <div className="bg-card rounded-xl border p-5">
          <h3 className="mb-3 font-medium">Connection Status</h3>
          <div className="flex items-center gap-3">
            <HealthBadge status={health.status} message={health.message} />
            <span className="text-muted-foreground text-sm">{health.message}</span>
          </div>
        </div>
      )}

      {/* Overview widgets */}
      {widgets.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {widgets.map((widget, i) => {
            if (widget.type === 'stat') return <StatWidget key={i} widget={widget} />;
            if (widget.type === 'link-card') {
              return (
                <LinkCardWidget
                  key={i}
                  widget={widget}
                  projectId={projectId}
                  storefrontOrigin={storefrontOrigin}
                />
              );
            }
            return null;
          })}
        </div>
      )}

      {/* Features */}
      <div className="bg-card rounded-xl border p-5">
        <h3 className="mb-3 font-medium">Features</h3>
        <div className="grid gap-2 sm:grid-cols-2">
          {manifest.features.map((feature, i) => (
            <div key={i} className="text-muted-foreground flex items-center gap-2 text-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              {feature}
            </div>
          ))}
        </div>
      </div>

      {/* Links */}
      {Object.keys(manifest.links).length > 0 && (
        <div className="bg-card rounded-xl border p-5">
          <h3 className="mb-3 font-medium">Resources</h3>
          <div className="flex flex-wrap gap-3">
            {/* No Documentation link — docs.tyashin.com doesn't exist yet.
                Re-add here (and in the registry `links.docs`) once a real
                docs site is live; never ship a dead link. */}
            {manifest.links.website && (
              <a
                href={manifest.links.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Website
              </a>
            )}
            {manifest.links.support && (
              <a
                href={manifest.links.support}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Support
              </a>
            )}
            {manifest.links.privacy && (
              <a
                href={manifest.links.privacy}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Privacy Policy
              </a>
            )}
          </div>
        </div>
      )}

      {/* Install info */}
      {installed && (
        <div className="bg-card rounded-xl border p-5">
          <h3 className="mb-3 font-medium">Installation</h3>
          <div className="text-muted-foreground space-y-1 text-sm">
            <p>Installed: {new Date(installed.installedAt).toLocaleDateString()}</p>
            <p>Status: {installed.isEnabled ? 'Active' : 'Disabled'}</p>
            {manifest.dataPolicy.gdprRelevant && (
              <p className="text-yellow-600 dark:text-yellow-400">
                &#9888; This plugin processes personal data (GDPR relevant)
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
